// This type maps to the GraphQL AppUser schema
export type AppUser = {
  id: string;
  demographics?: string | Demographics; // AWSJSON, stored as stringified JSON
  userGroups?: string[];
  usernames?: string; // AWSJSON, stored as stringified JSON
  streak?: number;
  badges?: string[];
  tasks?: string[];
  goals?: string[];
  createdAt?: string;
  updatedAt?: string;
};

export type SexOption = 'female' | 'male' | 'prefer_not_to_answer';
export type GenderOption =
  | 'cisgender'
  | 'transgender'
  | 'nonbinary'
  | 'prefer_not_to_answer';
export type RaceOption =
  | 'white'
  | 'black'
  | 'native_american'
  | 'asian'
  | 'pacific_islander'
  | 'other'
  | 'prefer_not_to_answer';
export type EthnicityOption = 'hispanic_latino' | 'non_hispanic_latino';
export type EmploymentOption =
  | 'full_time'
  | 'part_time'
  | 'student'
  | 'disabled'
  | 'retired'
  | 'prefer_not_to_answer';
export type DisabilityOption = 'yes' | 'no' | 'prefer_not_to_answer';
export type EducationOption =
  | 'some_high_school'
  | 'high_school'
  | 'some_college'
  | 'associates'
  | 'bachelors'
  | 'postgraduate'
  | 'prefer_not_to_answer';
export type AreaOption = 'urban' | 'suburban' | 'rural' | 'prefer_not_to_answer';

export const SEX_LABELS: Record<SexOption, string> = {
  female: 'Female',
  male: 'Male',
  prefer_not_to_answer: 'Prefer not to answer',
};

export const GENDER_LABELS: Record<GenderOption, string> = {
  cisgender: 'Cisgender',
  transgender: 'Transgender',
  nonbinary: 'Non-binary',
  prefer_not_to_answer: 'Prefer not to answer',
};

export const RACE_LABELS: Record<RaceOption, string> = {
  white: 'White / Caucasian',
  black: 'Black / African American',
  native_american: 'American Indian / Alaskan Native',
  asian: 'Asian',
  pacific_islander: 'Native Hawaiian or Other Pacific Islander',
  other: 'Other Race',
  prefer_not_to_answer: 'Prefer not to answer',
};

export const ETHNICITY_LABELS: Record<EthnicityOption, string> = {
  hispanic_latino: 'Hispanic or Latino/a',
  non_hispanic_latino: 'Non-Hispanic or Latino/a',
};

export const EMPLOYMENT_LABELS: Record<EmploymentOption, string> = {
  full_time: 'Full time employment',
  part_time: 'Part time employment',
  student: 'Student',
  disabled: 'Disabled',
  retired: 'Retired',
  prefer_not_to_answer: 'Prefer not to answer',
};

export const DISABILITY_LABELS: Record<DisabilityOption, string> = {
  yes: 'Yes',
  no: 'No',
  prefer_not_to_answer: 'Prefer not to answer',
};

export const EDUCATION_LABELS: Record<EducationOption, string> = {
  some_high_school: 'Some high school',
  high_school: 'High school or GED',
  some_college: 'Some college',
  associates: 'Associates degree',
  bachelors: 'Bachelors degree',
  postgraduate: 'Post-graduate training (Masters, Doctorate)',
  prefer_not_to_answer: 'Prefer not to answer',
};

export const AREA_LABELS: Record<AreaOption, string> = {
  urban: 'Urban',
  suburban: 'Suburban',
  rural: 'Rural',
  prefer_not_to_answer: 'Prefer not to answer',
};

export const DEMOGRAPHIC_LABEL_MAPS: Record<string, Record<any, string>> = {
  biologicalSex: SEX_LABELS,
  genderIdentity: GENDER_LABELS,
  race: RACE_LABELS,
  ethnicity: ETHNICITY_LABELS,
  employmentStatus: EMPLOYMENT_LABELS,
  disability: DISABILITY_LABELS,
  education: EDUCATION_LABELS,
  childhoodArea: AREA_LABELS,
  currentResidence: AREA_LABELS,
  sex: SEX_LABELS,
};

export type Demographics = {
  age?: number;
  biologicalSex?: SexOption;
  childhoodArea?: AreaOption;
  conditions?: string[];
  currentResidence?: AreaOption;
  dateOfBirth?: string;
  disability?: DisabilityOption;
  education?: EducationOption;
  employmentStatus?: EmploymentOption[];
  ethnicity?: EthnicityOption;
  genderIdentity?: GenderOption;
  heightFeet?: number;
  heightInches?: number;
  medications?: string[];
  race?: RaceOption[];
  sex?: SexOption;
  weightLbs?: number;
};

export const DEMOGRAPHICS_LABELS: Record<string, string> = {
  age: 'Age',
  biologicalSex: 'Biological Sex',
  childhoodArea: 'Childhood Area',
  conditions: 'Conditions',
  currentResidence: 'Current Residence',
  dateOfBirth: 'Date of Birth',
  disability: 'Disability',
  education: 'Education',
  employmentStatus: 'Employment Status',
  ethnicity: 'Ethnicity',
  genderIdentity: 'Gender Identity',
  heightFeet: 'Height (Feet)',
  heightInches: 'Height (Inches)',
  medications: 'Medications',
  race: 'Race',
  sex: 'Sex',
  weightLbs: 'Weight (Lbs)',
};

export default AppUser;